import React, {
	useRef,
	useState,
	useEffect,
	useLayoutEffect,
	useContext,
} from 'react';
import withAppContext from 'tbk-components/src/hooks/withAppContext';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Button, { ILink } from 'tbk-components/src/components/Button';
import AnimatedArcs from '../AnimatedArcs';
import FormLightboxButton, {
	IFormLightboxButtonProps,
} from '../FormLightboxButton';
import classNames from 'classnames';
import { IVideo } from '../Video';
import { AnimationOnScroll } from 'react-animation-on-scroll';

export interface IMainHeroCareerCardProps {
	image?: IImage;
	openPositions?: number;
	openPositionsText?: string;
	careerCardLink?: ILink;
	order?: 'Default' | 1 | 2 | 3;
}

export interface IMainHeroInvestorsCardProps {
	image?: IImage;
	eyebrow?: string;
	heading?: string;
	link?: ILink;
	order?: 'Default' | 1 | 2 | 3;
}

export interface IMainHeroPostCardProps {
	image?: IImage;
	postType?: string;
	postHeading?: string;
	postLink?: ILink;
	postReadTime?: string;
	order?: 'Default' | 1 | 2 | 3;
}

export interface IMainHeroContentCardsProps extends IBaseElementProps {
	/**
	 * Tagline text before the heading.
	 * @control richtext
	 */
	tagline?: string;
	/**
	 * Heading text - bold text will have color gradient applied
	 * @control richtext
	 */
	heading?: string;
	subHeading?: string;
	backgroundImage?: IImage;
	backgroundVideo?: IVideo;
	autoPlay?: boolean;
	backgroundVideoPlayLabel?: string;
	backgroundVideoPauseLabel?: string;
	ctaButton?: ILink;
	formLightboxButton?: IFormLightboxButtonProps;
	inverted?: boolean;
	careerCard?: IMainHeroCareerCardProps;
	investorsCard?: IMainHeroInvestorsCardProps;
	postCard?: IMainHeroPostCardProps;
	arcsPosition: 'Left' | 'Right';
	disableAnimation?: boolean;
}

/**
 * Calian Main Hero - Content Cards
 * @icon cover-image
 * @block
 */
const MainHeroContentCards: React.FC<IMainHeroContentCardsProps> =
	withAppContext(
		({
			tagline,
			heading,
			subHeading,
			backgroundImage,
			backgroundVideo,
			autoPlay = true,
			backgroundVideoPlayLabel = 'Play',
			backgroundVideoPauseLabel = 'Pause',
			ctaButton,
			formLightboxButton,
			careerCard,
			investorsCard,
			postCard,
			arcsPosition = 'Left',
			disableAnimation = false,
			context,
			inverted = false,
		}) => {
			const bgVideoRef = useRef(null);
			const bgVideoBtnRef = useRef(null);
			const [isVideoPlaying, setIsVideoPlaying] = useState(autoPlay);

			const handleVideoPlay = (videoRef) => {
				setIsVideoPlaying(true);
				videoRef.play();
			};

			const handleVideoPause = (videoRef) => {
				setIsVideoPlaying(false);
				videoRef.pause();
			};

			const careerCardRender = React.createElement(
				careerCard &&
					(careerCard?.openPositions ||
						careerCard?.image ||
						careerCard?.openPositionsText) &&
					careerCard?.careerCardLink?.href
					? Button
					: 'div',
				{
					className: cn(
						'main-hero-content-cards__card bg-white rounded-5 d-flex flex-row align-items-center gap-3 gap-lg-5 mb-3 p-4',
					),
					href: careerCard?.careerCardLink?.href,
					target: careerCard?.careerCardLink?.target,
					children: (
						<>
							{careerCard?.image && (
								<div className="main-hero-content-cards__card-image min-width-120px mw-120px min-width-sm-156px mw-sm-156px">
									<Image
										className="img-fluid w-100 h-auto object-fit-cover rounded-4"
										{...careerCard?.image}
									/>
								</div>
							)}
							<div className="main-hero-content-cards__card-content">
								{careerCard?.openPositions && (
									<p className="main-hero-content-cards__card-open-positions fs-4 mb-2">
										<span className="fw-bold me-2">
											{careerCard?.openPositions}
										</span>

										{careerCard?.openPositionsText}
									</p>
								)}
								{careerCard?.careerCardLink.href && (
									<span className="main-hero-content-cards__card-link link-primary text-decoration-none fs-18px">
										<span className="link-text">
											{careerCard.careerCardLink.label}
										</span>
										<span className="icon icon-arrow-right fs-7 ms-1 pb-1"></span>
									</span>
								)}
							</div>
						</>
					),
				},
			);

			const investorsCardRender = React.createElement(
				investorsCard &&
					(investorsCard?.image ||
						investorsCard?.eyebrow ||
						investorsCard?.heading) &&
					investorsCard?.link?.href
					? Button
					: 'div',
				{
					className: cn(
						'main-hero-content-cards__card bg-white rounded-5 d-flex flex-row align-items-center gap-3 gap-lg-5 mb-3 p-4 pe-2',
					),
					href: investorsCard?.link?.href,
					target: investorsCard?.link?.target,
					children: (
						<>
							{investorsCard?.image && (
								<div className="main-hero-content-cards__card-image min-width-120px mw-120px min-width-sm-156px mw-sm-156px">
									<Image
										className="img-fluid w-100 h-auto object-fit-cover rounded-4"
										{...investorsCard?.image}
									/>
								</div>
							)}
							<div className="main-hero-content-cards__card-content mb-2">
								{investorsCard?.eyebrow && (
									<p className="main-hero-content-cards__card-post-type text-mid-grey fs-15px mb-2">
										{investorsCard?.eyebrow}
									</p>
								)}

								{investorsCard?.heading && (
									<>
										{investorsCard?.link?.href ? (
											<span className="main-hero-content-cards__card-post-heading text-decoration-none link-charcoal d-inline-block h6 mb-2 pb-1 lh-2">
												<span className="link-text">
													{investorsCard?.heading}
												</span>
												<span className="icon icon-arrow-right text-primary fs-7 ms-1 pb-1"></span>
											</span>
										) : (
											<p className="main-hero-content-cards__card-post-heading h6 mb-2 pb-1 ">
												{investorsCard?.heading}
											</p>
										)}
									</>
								)}
							</div>
						</>
					),
				},
			);

			const postCardRender = React.createElement(
				postCard &&
					(postCard?.image ||
						postCard?.postType ||
						postCard?.postHeading ||
						postCard?.postReadTime) &&
					postCard?.postLink?.href
					? Button
					: 'div',
				{
					className: cn(
						'main-hero-content-cards__card bg-white rounded-5 d-flex flex-row align-items-center gap-3 gap-lg-5 mb-3 p-4 pe-2',
					),
					href: postCard?.postLink?.href,
					target: postCard?.postLink?.target,
					children: (
						<>
							{postCard?.image && (
								<div className="main-hero-content-cards__card-image min-width-120px mw-120px min-width-sm-156px mw-sm-156px">
									<Image
										className="img-fluid w-100 h-auto object-fit-cover rounded-4"
										{...postCard?.image}
									/>
								</div>
							)}
							<div className="main-hero-content-cards__card-content mb-2">
								{postCard?.postType && (
									<p className="main-hero-content-cards__card-post-type text-mid-grey fs-15px mb-2">
										{postCard?.postType}
									</p>
								)}

								{postCard?.postHeading && (
									<>
										{postCard?.postLink?.href ? (
											<span className="main-hero-content-cards__card-post-heading text-decoration-none link-charcoal d-inline-block h6 mb-2 pb-1 lh-2">
												<span className="link-text">
													{postCard?.postHeading}
												</span>
												<span className="icon icon-arrow-right text-primary fs-7 ms-1 pb-1"></span>
											</span>
										) : (
											<p className="main-hero-content-cards__card-post-heading h6 mb-2 pb-1 ">
												{postCard?.postHeading}
											</p>
										)}
									</>
								)}
								{postCard?.postReadTime && (
									<p className="main-hero-content-cards__card-post-read-time text-mid-grey small fs-13px mb-0 ">
										{postCard?.postReadTime}
									</p>
								)}
							</div>
						</>
					),
				},
			);
			const animationCardDelays = [500, 700, 900];
			return (
				<div className="calian-main-hero position-relative">
					<div className="main-hero-content-cards">
						<div className="main-hero-content-cards__content position-relative z-1 container-fluid mw-1206px pt-5 pb-4 pt-lg-10 pb-lg-8">
							<div className="row mt-7 pt-2">
								<div className="col-12 col-lg-6 col-xl-7 d-lg-flex flex-lg-column align-items-start justify-content-center mb-3 mb-lg-0 ps-xl-1 pe-xl-9">
									<AnimationOnScroll
										animateIn={
											!disableAnimation
												? 'fadeInUp'
												: 'none'
										}
										animateOnce={true}
										delay={!disableAnimation ? 500 : 0}
									>
										{tagline && (
											<p className="main-hero-content-cards__tagline fs-4 ls-1 mb-3">
												<span
													dangerouslySetInnerHTML={{
														__html: tagline,
													}}
												/>
											</p>
										)}
										{heading && (
											<h1 className="main-hero-content-cards__heading mb-4">
												<span
													dangerouslySetInnerHTML={{
														__html: heading,
													}}
												/>
											</h1>
										)}
										{subHeading && (
											<p className="main-hero-content-cards__subheading subheading mb-4">
												{subHeading}
											</p>
										)}
										{ctaButton &&
											ctaButton?.label &&
											ctaButton?.label.length > 0 && (
												<Button
													className="main-hero-content-cards__cta-button btn btn-primary"
													{...ctaButton}
												/>
											)}
										{formLightboxButton?.formLightboxButton
											?.label?.length > 0 && (
											<FormLightboxButton
												{...formLightboxButton}
											/>
										)}
									</AnimationOnScroll>
								</div>

								<div className="col-12 col-lg-6 col-xl-5 pe-xl-0">
									<div className="main-hero-content-cards__card-listing d-flex flex-column">
										<div
											className={
												careerCard?.order !==
													'Default' &&
												careerCard?.order
													? `order-${careerCard?.order}`
													: 'order-1'
											}
										>
											<AnimationOnScroll
												animateIn={
													!disableAnimation
														? 'fadeInUp'
														: 'none'
												}
												animateOnce={true}
												delay={
													disableAnimation
														? 0
														: careerCard?.order !==
																'Default' &&
														  animationCardDelays[
																careerCard?.order -
																	1
														  ]
														? animationCardDelays[
																careerCard?.order -
																	1
														  ]
														: animationCardDelays[0]
												}
											>
												{careerCardRender}
											</AnimationOnScroll>
										</div>
										<div
											className={
												investorsCard?.order !==
													'Default' &&
												investorsCard?.order
													? `order-${investorsCard?.order}`
													: 'order-2'
											}
										>
											<AnimationOnScroll
												animateIn={
													!disableAnimation
														? 'fadeInUp'
														: 'none'
												}
												animateOnce={true}
												delay={
													disableAnimation
														? 0
														: investorsCard?.order !==
																'Default' &&
														  animationCardDelays[
																investorsCard?.order -
																	1
														  ]
														? animationCardDelays[
																investorsCard?.order -
																	1
														  ]
														: animationCardDelays[1]
												}
											>
												{investorsCardRender}
											</AnimationOnScroll>
										</div>
										<div
											className={
												postCard?.order !== 'Default' &&
												postCard?.order
													? `order-${postCard?.order}`
													: 'order-3'
											}
										>
											<AnimationOnScroll
												animateIn={
													!disableAnimation
														? 'fadeInUp'
														: 'none'
												}
												animateOnce={true}
												delay={
													disableAnimation
														? 0
														: postCard?.order !==
																'Default' &&
														  animationCardDelays[
																postCard?.order -
																	1
														  ]
														? animationCardDelays[
																postCard?.order -
																	1
														  ]
														: animationCardDelays[2]
												}
											>
												{postCardRender}
											</AnimationOnScroll>
										</div>
									</div>
								</div>
							</div>
						</div>
						<AnimatedArcs
							arcsStyle={'Hero - Three Lines'}
							arcsPosition={arcsPosition}
							arcOneColor="var(--bs-white)"
							arcOneStrokeWidth={4}
							arcTwoColor="var(--bs-white)"
							arcTwoStrokeWidth={3}
							arcThreeColor="var(--bs-white)"
							arcThreeStrokeWidth={2}
							animationDelay={1000}
							animationOffset={150}
						/>
					</div>
					<div className="main-hero-content-cards__overlay-gradient position-absolute w-100 h-100 top-0 start-0"></div>
					{!backgroundVideo && backgroundImage && (
						<div
							className="main-hero-content-cards__overlay-bg position-absolute w-100 h-100 top-0 start-0"
							style={{
								backgroundImage: `url(${backgroundImage?.src})`,
							}}
						></div>
					)}

					{backgroundVideo && (
						<>
							<video
								src={backgroundVideo.src}
								className="main-hero-content-cards__bg-video"
								poster={backgroundImage?.src}
								autoPlay
								muted
								playsInline
								loop
								ref={bgVideoRef}
							/>

							<button
								className="hero-banner__bg-video-btn btn link-charcoal position-absolute z-3 bottom-0 left-0 d-flex justify-content-center align-items-center fs-9 ms-3 ms-lg-5 mb-3 mb-lg-5 p-1"
								tabIndex={0}
								ref={bgVideoBtnRef}
								onClick={() => {
									const video =
										bgVideoRef.current! as HTMLVideoElement;
									video.paused
										? handleVideoPlay(video)
										: handleVideoPause(video);
								}}
							>
								{!isVideoPlaying ? (
									<>
										<span className="icon icon-play1 fs-8px me-2"></span>
										{backgroundVideoPlayLabel}
									</>
								) : (
									<>
										<span className="icon icon-pause1 fs-8px me-2"></span>
										{backgroundVideoPauseLabel}
									</>
								)}
							</button>
						</>
					)}
				</div>
			);
		},
	);

export default MainHeroContentCards;
